import { framedImageStyle, imageUuid, readFocalPoint, resolveFramingSource } from './adFraming';

/**
 * 🔴 Story 12.15 — WHAT A LISTING CARD FRAMES FROM. ONE PLACE, ALL ELEVEN
 * SURFACES.
 *
 * Read `resolveFramingSource`'s header in `util/adFraming.js` first: it carries
 * the source-dimension seam this module's callers inherit the moment they start
 * calling `framedImageStyle`.
 *
 * 🔴 THE FACT THAT SHAPES THIS MODULE. A card cannot honour a focal point
 * against the `listing-card` / `listing-card-2x` derivatives, because those are
 * built with `fit: 'crop'` (`sdkLoader.js`) plus imgix `crop=edges`, so the
 * pixels the focal was normalised against are already gone. The repo says this
 * in its own words at the choke point that fixed it for the wizard:
 *
 *   "The two `variantPrefix` derivatives are both 4:3-CROPPED, and framing
 *    against an already-cropped image is dishonest."
 *   — `EditListingPage.duck.js`
 *
 * So a card frames only from an ASPECT-PRESERVING variant, and renders
 * UNFRAMED — plain `object-fit: cover`, exactly as it does today — when it has
 * none. This mirrors `tileFraming`'s shipped rule verbatim.
 *
 * ⚠️ THIS MODULE IS PURE. It must not import the SDK: `ListingCard` is on the
 * highest-traffic page in the marketplace and the `frame-*` REQUEST definitions
 * (which do need `createImageVariantConfig`) live next door in
 * `util/cardImageVariants.js`, imported by the ducks only.
 */

/**
 * 🔴 ORDER IS DELIBERATE, AND THE `scaled-*` PAIR IS LOAD-BEARING.
 *
 * `frame-2x` / `frame-1x` are the custom `fit:'scale'` variants the PUBLIC
 * ducks request. They are a BANDWIDTH OPTIMISATION, not the mechanism: they are
 * right-sized for a card box, where the built-in `scaled-small` is only 320 px
 * on its long edge and forces the browser up to the 750 px `scaled-medium`
 * (+1,290 KB per 24-card page, measured).
 *
 * `scaled-xlarge` / `scaled-large` are aspect-preserving BUILT-INS that are
 * ALREADY on every wizard image entity (`EditListingPage.duck.js`'s
 * `getImageVariantInfo`, the single choke point feeding all six upload call
 * sites) and cost no new query parameter. They are also the exact list
 * `tileFraming` frames from. With them here, the two SELLER-FACING card
 * surfaces — `PostPublishScreen` and the Step-3 card preview — frame correctly
 * with ZERO duck changes.
 *
 * 🔴 Without them both surfaces resolve nothing framable and fail SILENTLY,
 * FOREVER: the celebration card renders unframed and the Step-3 preview shows
 * "Still preparing this photo" on every listing, with nothing thrown and
 * nothing red. `EditListingPage.duck.js` is deliberately NOT wired for
 * `frame-*`; do not "complete the set" and do not trim this list.
 *
 * On a public surface all four may be present and `frame-2x` wins, so the grid
 * still gets the right-sized file. No surface ever downloads a variant it did
 * not already request.
 */
export const CARD_FRAMING_VARIANTS = ['frame-2x', 'frame-1x', 'scaled-xlarge', 'scaled-large'];

/**
 * DISPLAY ONLY. Pre-cropped (`fit: 'crop'` + `crop=edges`). A card resolving one
 * of these still shows the photo — it simply does not claim a framing it cannot
 * honour. This is the normal state for a just-uploaded cover and resolves itself
 * when the aspect-preserving derivative lands.
 */
export const CARD_FALLBACK_VARIANTS = ['listing-card-2x', 'listing-card'];

/** Display preference: frame if we can, show the photo either way. Never blank. */
export const CARD_DISPLAY_VARIANTS = [...CARD_FRAMING_VARIANTS, ...CARD_FALLBACK_VARIANTS];

/**
 * The shipped `pickVariant` shape (`YourAdCard.js`, then `EditListingPhotosForm.js`),
 * EXTRACTED here rather than duplicated a third time: preference list in, first
 * usable variant out, `null` when none. A second implementation is a review
 * failure.
 */
const isUsableVariant = variant =>
  !!variant &&
  typeof variant.url === 'string' &&
  Number.isFinite(variant.width) &&
  Number.isFinite(variant.height) &&
  variant.width > 0 &&
  variant.height > 0;

export const pickVariant = (image, preference) => {
  const variants = image?.attributes?.variants;
  if (!variants) {
    return null;
  }
  for (const name of preference) {
    const variant = variants[name];
    if (isUsableVariant(variant)) {
      return { name, url: variant.url, width: variant.width, height: variant.height };
    }
  }
  return null;
};

/**
 * 🔴 THE ONE PLACE THAT DECIDES WHAT A CARD FRAMES FROM.
 *
 * Eight list containers need this to key their decoded-size map, and the
 * Step-3 preview needs it to decide between the card and its "still preparing"
 * line. Left unnamed, the preference list gets copy-pasted into all of them —
 * the exact second-implementation hazard this module exists to prevent, one
 * layer up.
 *
 * @returns {{name: string, url: string, width: number, height: number}|null}
 *   `null` means: do not frame (and, for the wizard preview, this photo's
 *   derivatives have not landed yet).
 */
export const cardFramingVariant = image => pickVariant(image, CARD_FRAMING_VARIANTS);

/** Any variant a card may display, framable or not. `null` = no photo to show. */
export const cardDisplayVariant = image => pickVariant(image, CARD_DISPLAY_VARIANTS);

/**
 * The framing URLs for a list of listings, keyed the way a container's decoded
 * map is keyed. `SearchResultsPanel` and friends call this; none of them
 * re-derives the preference.
 *
 * @returns {Array<string>} deduped, order-preserving, no nulls
 */
export const cardFramingUrls = (listings = []) => {
  const seen = new Set();
  (Array.isArray(listings) ? listings : []).forEach(listing => {
    const url = cardFramingVariant(listing?.images?.[0])?.url;
    if (url) {
      seen.add(url);
    }
  });
  return [...seen];
};

/**
 * 🔴 The framed geometry for one card, and the `srcSet` names that may be
 * emitted underneath it.
 *
 * Mirrors `tileFraming` (`EditListingPhotosForm.js`) deliberately — same
 * degradation rule, same aspect-preserving-only `srcSet` rule — with ONE
 * difference the tiles do not have: `CARD_FRAMING_VARIANTS` spans TWO families
 * (`frame-*` on public surfaces, `scaled-*` on the wizard) and they do not
 * co-exist. `tileFraming` can emit its whole preference list because its two
 * entries always do. Here we must emit the INTERSECTION with what the image
 * actually carries — naming an absent variant makes `ResponsiveImage` drop it
 * and the `w`-descriptor ladder silently loses a rung.
 *
 * @param {Object}  args.image        a Sharetribe image entity
 * @param {Object} [args.focalPoints] `publicData.photoFocalPoints`
 * @param {number}  args.targetWidth  aspect UNITS are fine — `framedImageStyle`
 * @param {number}  args.targetHeight is scale-invariant. See AC-4(b).
 * @param {{width: number, height: number}} [args.decodedSize] the browser's own
 *   decode of the painted pixels. Absent ⇒ the variant's metadata is used, which
 *   is the pre-12.13 behaviour and is wrong for an EXIF-rotated portrait.
 * @returns {{variant, variantNames: string[]|undefined, style: Object|null}}
 */
export const cardFraming = ({ image, focalPoints, targetWidth, targetHeight, decodedSize }) => {
  const variant = cardDisplayVariant(image);
  if (!variant) {
    return { variant: null, variantNames: undefined, style: null };
  }
  if (!CARD_FRAMING_VARIANTS.includes(variant.name)) {
    // Display it, but do not claim a framing it cannot honour.
    return { variant, variantNames: [variant.name], style: null };
  }
  const present = image?.attributes?.variants || {};
  return {
    variant,
    // 🔴 Only the aspect-preserving names that are actually PRESENT, so the
    // browser's candidate selection can never land on a cropped derivative
    // underneath framed geometry.
    //
    // ⚠️ N-8, cold code review 2026-08-15 — this tested `!!present[name]`, i.e.
    // PRESENCE, while `pickVariant` two functions up tests USABILITY. A variant
    // that exists but carries no `url` or a zero `width` would have been named
    // here and then emitted as `undefined undefinedw` into a `srcSet`. One
    // validity rule, applied in both places.
    variantNames: CARD_FRAMING_VARIANTS.filter(name => isUsableVariant(present[name])),
    style: framedImageStyle({
      focal: readFocalPoint(focalPoints, imageUuid(image)),
      ...resolveFramingSource({
        variantWidth: variant.width,
        variantHeight: variant.height,
        decodedWidth: decodedSize?.width,
        decodedHeight: decodedSize?.height,
      }),
      targetWidth,
      targetHeight,
    }),
  };
};
